/**
 * String Utilities
 * 
 * Common string manipulation and formatting functions.
 * Provides consistent string handling across the application.
 * 
 * **Validates: Requirements 5.4, 5.5**
 */

/**
 * Capitalizes the first letter of a string
 * 
 * @param str - String to capitalize
 * @returns Capitalized string
 * 
 * @example
 * ```typescript
 * capitalize('hello world'); // "Hello world"
 * capitalize('HELLO'); // "HELLO"
 * ```
 */
export function capitalize(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Capitalizes the first letter of each word
 * 
 * @param str - String to capitalize
 * @returns Title-cased string
 * 
 * @example
 * ```typescript
 * capitalizeWords('hello world'); // "Hello World"
 * capitalizeWords('the quick brown fox'); // "The Quick Brown Fox"
 * ```
 */
export function capitalizeWords(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
}

/**
 * Truncates a string to a maximum length
 * 
 * @param str - String to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to append (default: '...')
 * @returns Truncated string
 * 
 * @example
 * ```typescript
 * truncate('Hello World', 8); // "Hello..."
 * truncate('Hello World', 8, '…'); // "Hello W…"
 * truncate('Hello', 10); // "Hello"
 * ```
 */
export function truncate(str: string, maxLength: number, suffix: string = '...'): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  if (str.length <= maxLength) {
    return str;
  }

  return str.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * Truncates a string at word boundaries
 * 
 * @param str - String to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to append (default: '...')
 * @returns Truncated string
 * 
 * @example
 * ```typescript
 * truncateWords('Hello World Example', 12); // "Hello World..."
 * ```
 */
export function truncateWords(str: string, maxLength: number, suffix: string = '...'): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  if (str.length <= maxLength) {
    return str;
  }

  const truncated = str.slice(0, maxLength - suffix.length);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > 0) {
    return truncated.slice(0, lastSpace) + suffix;
  }

  return truncated + suffix;
}

/**
 * Converts a string to a URL-friendly slug
 * 
 * @param str - String to slugify
 * @returns Slugified string
 * 
 * @example
 * ```typescript
 * slugify('Hello World!'); // "hello-world"
 * slugify('The Quick Brown Fox'); // "the-quick-brown-fox"
 * slugify('  Spaces   Everywhere  '); // "spaces-everywhere"
 * ```
 */
export function slugify(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove non-word chars
    .replace(/[\s_-]+/g, '-') // Replace spaces, underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Converts a string to camelCase
 * 
 * @param str - String to convert
 * @returns camelCase string
 * 
 * @example
 * ```typescript
 * camelCase('hello world'); // "helloWorld"
 * camelCase('Hello-World'); // "helloWorld"
 * camelCase('hello_world'); // "helloWorld"
 * ```
 */
export function camelCase(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase());
}

/**
 * Converts a string to PascalCase
 * 
 * @param str - String to convert
 * @returns PascalCase string
 * 
 * @example
 * ```typescript
 * pascalCase('hello world'); // "HelloWorld"
 * pascalCase('hello-world'); // "HelloWorld"
 * ```
 */
export function pascalCase(str: string): string {
  const camelCased = camelCase(str);
  return capitalize(camelCased);
}

/**
 * Converts a string to snake_case
 * 
 * @param str - String to convert
 * @returns snake_case string
 * 
 * @example
 * ```typescript
 * snakeCase('helloWorld'); // "hello_world"
 * snakeCase('HelloWorld'); // "hello_world"
 * ```
 */
export function snakeCase(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Converts a string to kebab-case
 * 
 * @param str - String to convert
 * @returns kebab-case string
 * 
 * @example
 * ```typescript
 * kebabCase('helloWorld'); // "hello-world"
 * kebabCase('HelloWorld'); // "hello-world"
 * ```
 */
export function kebabCase(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Sanitizes a string by removing HTML tags and special characters
 * 
 * @param str - String to sanitize
 * @param allowedTags - Array of allowed HTML tags (optional)
 * @returns Sanitized string
 * 
 * @example
 * ```typescript
 * sanitize('<script>alert("xss")</script>Hello'); // "Hello"
 * sanitize('<p>Hello <b>World</b></p>', ['p', 'b']); // "<p>Hello <b>World</b></p>"
 * ```
 */
export function sanitize(str: string, allowedTags: string[] = []): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  if (allowedTags.length === 0) {
    // Remove all HTML tags
    return str.replace(/<[^>]*>/g, '');
  }

  // Remove only non-allowed tags
  const allowedPattern = allowedTags.join('|');
  const regex = new RegExp(`<(?!\\/?(${allowedPattern})\\b)[^>]*>`, 'gi');
  return str.replace(regex, '');
}

/**
 * Escapes HTML special characters
 * 
 * @param str - String to escape
 * @returns Escaped string
 * 
 * @example
 * ```typescript
 * escapeHtml('<div>Hello & "World"</div>');
 * // "&lt;div&gt;Hello &amp; &quot;World&quot;&lt;/div&gt;"
 * ```
 */
export function escapeHtml(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  return str.replace(/[&<>"']/g, (char) => htmlEscapeMap[char]);
}

/**
 * Unescapes HTML special characters
 * 
 * @param str - String to unescape
 * @returns Unescaped string
 */
export function unescapeHtml(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  const htmlUnescapeMap: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
  };

  return str.replace(/&(?:amp|lt|gt|quot|#39);/g, (entity) => htmlUnescapeMap[entity]);
}

/**
 * Removes extra whitespace from a string
 * 
 * @param str - String to clean
 * @returns Cleaned string
 * 
 * @example
 * ```typescript
 * removeExtraSpaces('  Hello   World  '); // "Hello World"
 * ```
 */
export function removeExtraSpaces(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str.replace(/\s+/g, ' ').trim();
}

/**
 * Checks if a string contains only alphanumeric characters
 * 
 * @param str - String to check
 * @returns True if alphanumeric
 */
export function isAlphanumeric(str: string): boolean {
  if (!str || typeof str !== 'string') {
    return false;
  }

  return /^[a-zA-Z0-9]+$/.test(str);
}

/**
 * Checks if a string contains only alphabetic characters
 * 
 * @param str - String to check
 * @returns True if alphabetic
 */
export function isAlpha(str: string): boolean {
  if (!str || typeof str !== 'string') {
    return false;
  }

  return /^[a-zA-Z]+$/.test(str);
}

/**
 * Checks if a string contains only numeric characters
 * 
 * @param str - String to check
 * @returns True if numeric
 */
export function isNumeric(str: string): boolean {
  if (!str || typeof str !== 'string') {
    return false;
  }

  return /^[0-9]+$/.test(str);
}

/**
 * Reverses a string
 * 
 * @param str - String to reverse
 * @returns Reversed string
 * 
 * @example
 * ```typescript
 * reverse('hello'); // "olleh"
 * ```
 */
export function reverse(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str.split('').reverse().join('');
}

/**
 * Counts the number of words in a string
 * 
 * @param str - String to count words in
 * @returns Number of words
 * 
 * @example
 * ```typescript
 * wordCount('Hello World'); // 2
 * wordCount('The quick brown fox'); // 4
 * ```
 */
export function wordCount(str: string): number {
  if (!str || typeof str !== 'string') {
    return 0;
  }

  return str.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Extracts initials from a name
 * 
 * @param name - Name to extract initials from
 * @param maxInitials - Maximum number of initials (default: 2)
 * @returns Initials string
 * 
 * @example
 * ```typescript
 * getInitials('John Doe'); // "JD"
 * getInitials('John Michael Doe', 3); // "JMD"
 * ```
 */
export function getInitials(name: string, maxInitials: number = 2): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  const words = name.trim().split(/\s+/)
    .filter((word) => !/^\[.*\]$/.test(word)); // skip bracketed words like [System]
  const initials = words
    .slice(0, maxInitials)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

  return initials;
}

/**
 * Masks a string (e.g., for sensitive data)
 * 
 * @param str - String to mask
 * @param visibleStart - Number of visible characters at start
 * @param visibleEnd - Number of visible characters at end
 * @param maskChar - Character to use for masking (default: '*')
 * @returns Masked string
 * 
 * @example
 * ```typescript
 * mask('1234567890', 2, 2); // "12******90"
 * mask('secret@email.com', 3, 10, 'x'); // "secxxxxxxxxmail.com"
 * ```
 */
export function mask(
  str: string,
  visibleStart: number = 0,
  visibleEnd: number = 0,
  maskChar: string = '*'
): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  if (str.length <= visibleStart + visibleEnd) {
    return str;
  }

  const start = str.slice(0, visibleStart);
  const end = str.slice(-visibleEnd);
  const maskLength = str.length - visibleStart - visibleEnd;
  const masked = maskChar.repeat(maskLength);

  return start + masked + end;
}

/**
 * Pads a string to a specified length
 * 
 * @param str - String to pad
 * @param length - Target length
 * @param padChar - Character to use for padding (default: ' ')
 * @param padStart - Pad at start if true, end if false (default: true)
 * @returns Padded string
 * 
 * @example
 * ```typescript
 * pad('5', 3, '0'); // "005"
 * pad('5', 3, '0', false); // "500"
 * ```
 */
export function pad(
  str: string,
  length: number,
  padChar: string = ' ',
  padStart: boolean = true
): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  if (str.length >= length) {
    return str;
  }

  const padding = padChar.repeat(length - str.length);
  return padStart ? padding + str : str + padding;
}

/**
 * Checks if a string is empty or contains only whitespace
 * 
 * @param str - String to check
 * @returns True if empty or whitespace
 */
export function isEmpty(str: string | null | undefined): boolean {
  return !str || str.trim().length === 0;
}

/**
 * Checks if a string is not empty
 * 
 * @param str - String to check
 * @returns True if not empty
 */
export function isNotEmpty(str: string | null | undefined): boolean {
  return !isEmpty(str);
}
